import { useEffect, useRef, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Constants } from '@praktio/common';

import { RootState } from '../../ducks/configureStore';

import './ModuleTopBar.scss';
import { WindowSizes } from '../../utils/constants';

const { CustomNavType } = Constants;

interface IProps {
  moduleName: string;
  currentSlideNumber: number;
  totalSlideNumber: number;
  padTitle: boolean;
  windowSize: WindowSizes;
}

interface IProgressBarProps {
  percentage: number;
  windowSize: WindowSizes;
}

const ProgressIndicatorBar = ({ percentage, windowSize }: IProgressBarProps) => {
  if (windowSize === WindowSizes.Small) {
    return null;
  }
  return (
    <div className="ProgressIndicatorBar">
      <div className="progress-indicator-inner" style={{ width: `${percentage}%` }}></div>
    </div>
  );
};

const ModuleTopBar = ({
  moduleName,
  currentSlideNumber,
  totalSlideNumber,
  padTitle,
  windowSize,
}: IProps) => {
  // REDUX STATE
  const { adaptiveMessage, slideId, isCustomNav } = useSelector((state: RootState) => {
    const { slidesLitmos } = state;
    const { currentSlide } = slidesLitmos;
    const { learnerData, _id: slideId } = currentSlide || {};
    const { adaptive: adaptiveMessage } = learnerData || {};
    const isCustomNav = currentSlide?.type === CustomNavType;
    return {
      adaptiveMessage,
      slideId,
      isCustomNav,
    };
  });

  // LOCAL STATE
  const [showAdaptiveMessage, setShowAdaptiveMessage] = useState(!!adaptiveMessage);
  const prevSlideId = useRef(slideId);

  // If the slide number changes, hide the adaptive message. Then, check to see if the new slide
  // has an adaptive message. If so, show the message.
  useEffect(() => {
    if (prevSlideId.current !== slideId) {
      setShowAdaptiveMessage(!!adaptiveMessage);
    }

    prevSlideId.current = slideId;
  }, [adaptiveMessage, slideId]);

  const percentage =
    totalSlideNumber > 0 ? Math.floor((currentSlideNumber / totalSlideNumber) * 100) : 0;
  const adaptiveMessageRef = useRef(null);

  return (
    <div className="ModuleTopBar flex-row vert-align-center">
      <div className="left-content flex-row vert-align-center">
        {/* Module Name */}
        <h1 className={classNames('module-name', { 'module-name-padding': padTitle })}>
          {moduleName}
        </h1>
      </div>

      {isCustomNav === false && (
        <div className="right-content flex-row vert-align-center">
          {/* Progress Bar */}
          <ProgressIndicatorBar percentage={percentage} windowSize={windowSize} />

          {/* Progress Text */}
          <div className="progress-indicator-text" ref={adaptiveMessageRef}>
            {currentSlideNumber}/{totalSlideNumber}
          </div>
          <Overlay
            target={adaptiveMessageRef.current}
            show={showAdaptiveMessage}
            placement={'bottom'}
          >
            {(props) => (
              <Popover id="adaptive-tooltip" {...props}>
                <Popover.Content>{adaptiveMessage}</Popover.Content>
                <div className="close-tooltip" onClick={() => setShowAdaptiveMessage(false)}>
                  <span className="material-icons">close</span>
                </div>
              </Popover>
            )}
          </Overlay>
        </div>
      )}
    </div>
  );
};

export default ModuleTopBar;
